<template>
  <div class="d-flex justify-content-center align-items-center signin-page" style="height: calc(100vh - 84px);">
  <div>
    <div style="display: flex; justify-content: center; width: 100%">
      <img src="/logo.png" height="25" class="mb-4" />
    </div>
    <div class="card card-md">
      <form class="card-body" @submit.prevent="initSignin">
        <h2 class="card-title text-center mb-4">Login to your account</h2>
        <div class="mb-3" v-if="!twoFactor">
          <label class="form-label" style="text-align: left">Email</label>
          <input
            v-model="user.email"
            type="email"
            class="form-control"
            placeholder="Enter email"
            autocomplete="username webauthn"
          />
        </div>
        <div class="mb-3" v-if="!twoFactor">
          <label class="form-label" style="text-align: left">Password</label>
          <input
            v-model="user.password"
            type="password"
            class="form-control"
            placeholder="Password"
            autocomplete="password"
          />
        </div>

        <div class="mb-3" v-if="twoFactor">
          <label class="form-label" style="text-align: left">Code from authenticator or recovery code</label>
          <input
            ref="totp"
            v-model="user.code"
            type="tel"
            class="form-control"
            placeholder="Code from authenticator or recovery code"
            autocomplete="one-time-code"
            name="totp"
            id="totp"
            inputmode="numeric"
            pattern="[0-9]*"
          />
        </div>
        <turnstile
          v-show="!twoFactor"
          ref="turnstile"
          class="captcha-widget"
          :sitekey="sitekey"
          @verify="verify"
          @expired="expired"
        ></turnstile>
        <div class="form-footer">
          <button
            :disabled="(!user.email || !user.password || isRobot) && !twoFactor"
            type="submit"
            class="btn btn-primary w-100"
          >
            Signin
          </button>
        </div>
        <div class="mt-2" v-if="!twoFactor && passkeysSupported">
          <button
            type="button"
            class="btn btn-outline-primary w-100"
            @click="passkeySignin(false)"
          >
            Sign in with a passkey
          </button>
        </div>
      </form>
    </div>
    <div class="text-center text-muted mt-3">
      Do not have account? <router-link to="/signup"> Sign up </router-link>
    </div>
    <div class="text-center text-muted mt-3">
      Forgot password?
      <router-link to="/password-recovery"> Recover Password </router-link>
    </div>
  </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex'
import {
	browserSupportsWebAuthn,
	browserSupportsWebAuthnAutofill,
	WebAuthnAbortService
} from '@simplewebauthn/browser'
import Turnstile from '@/components/Turnstile.vue'
import { signinWithPasskey } from '@/api/passkeys'

export default {
	name: 'Signin',
	data () {
		return {
			sitekey: process.env.VUE_APP_TURNSTILE_SITEKEY,
			user: {
				email: null,
				password: null,
				turnstile: null,
				code: ''
			},
			isRobot: true,
			robocheckBypassed: false,
			twoFactor: false,
			passkeysSupported: browserSupportsWebAuthn(),
			autofillSupported: false,
			autofillTimer: null,
			passkeyAttempt: 0
		}
	},
	components: {
		Turnstile
	},
	computed: {
		...mapState({
			origin: (state) => state.origin
		})
	},
	mounted () {
		// Check if dev mode is enabled
		fetch('/api/robochecker')
			.then(response => response.json())
			.then(data => {
				if (data.Data && data.Data.bypass === true) {
					this.robocheckBypassed = true
					this.isRobot = false
					console.log('Dev mode enabled - captcha bypassed')
				}
			})
			.catch(err => {
				console.error('Failed to check dev mode:', err)
			})
		browserSupportsWebAuthnAutofill().then((supported) => {
			this.autofillSupported = supported
			this.startAutofill()
		})
	},
	beforeUnmount () {
		this.passkeyAttempt++
		clearTimeout(this.autofillTimer)
		WebAuthnAbortService.cancelCeremony()
	},
	methods: {
		...mapActions(['signin']),
		...mapMutations(['loginSuccess']),
		afterLogin (userData) {
			this.loginSuccess(userData)
			const authRequestID = this.$route.query.authRequestID
			if (authRequestID) {
				window.location.replace('/oidc/login?authRequestID=' + encodeURIComponent(authRequestID))
			} else if (this.origin) {
				window.location.replace(window.location.protocol + '//' + this.origin + '/sso')
			} else {
				this.$router.push('/')
			}
		},
		startAutofill () {
			if (this.autofillSupported) {
				this.passkeySignin(true)
			}
		},
		passkeySignin (useBrowserAutofill) {
			clearTimeout(this.autofillTimer)
			const attempt = ++this.passkeyAttempt
			const isCurrent = () => attempt === this.passkeyAttempt
			if (useBrowserAutofill) {
				// The server forgets a ceremony after 5 minutes while the browser keeps
				// offering it, so a fresh one takes its place before then.
				this.autofillTimer = setTimeout(() => this.startAutofill(), 4 * 60 * 1000)
			}
			signinWithPasskey(useBrowserAutofill, isCurrent)
				.then((data) => {
					if (data.Status === 'ok') {
						this.afterLogin(data.Data)
					}
				})
				.catch((err) => {
					// A newer attempt aborted this one and now owns the page.
					if (!isCurrent()) {
						return
					}
					const rejectedByServer = err?.config?.url === '/signin/passkey/finish'
					// Autofill needs a new ceremony when the button aborted it, or when the
					// server used up the ceremony of a rejected passkey. It does not restart
					// after its own failed begin, because that would loop.
					if (!useBrowserAutofill || rejectedByServer) {
						this.startAutofill()
					}
					// A closed dialog is not an error.
					if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
						return
					}
					// Autofill starts on its own with every page load, so only a passkey the
					// user actually picked and the server rejected is worth a message.
					if (useBrowserAutofill && !rejectedByServer) {
						return
					}
					this.$swal.fire({
						icon: 'error',
						text: err?.response?.data?.Data || err?.message
					})
				})
		},
		verify (response) {
			if (response) {
				this.isRobot = false
				this.user.turnstile = response
			} else {
				this.isRobot = true
				this.user.turnstile = null
			}
		},
		expired () {
			this.isRobot = true
			this.user.turnstile = null
		},
		resetTurnstile () {
			if (this.robocheckBypassed) return
			this.$refs.turnstile.reset()
		},
		initSignin () {
			if ((!this.user.email || !this.user.password || this.isRobot) && !this.twoFactor) {
				return
			}
			if (this.isRobot && !this.twoFactor) {
				this.$swal.fire({
					icon: 'error',
					text: 'Please verify that you are not a robot!'
				})
				return
			}
			if (!this.user.email || !this.user.password) {
				this.$swal.fire({
					icon: 'error',
					text: 'Please enter username and password.'
				})
				return
			}
			this.signin(this.user)
				.then((data) => {
					if (
						data.Status === 'ok' &&
            data.Data === 'Enter Two Factor code'
					) {
						if (this.twoFactor) {
							this.$swal.fire({
								icon: 'error',
								text: 'Please enter two factor code.'
							})
						} else {
							this.twoFactor = true
							this.$nextTick(() => {
								this.$refs.totp?.focus()
							})
						}
						this.resetTurnstile()
					} else if (data.Status === 'ok') {
						this.afterLogin(data.Data)
					}
				})
				.catch((err) => {
					this.resetTurnstile()
					if (err?.response?.data?.Data === 'account blocked') {
						this.$router.push('/email-verification')
						return
					}
					this.$swal.fire({
						icon: 'error',
						text: err?.response?.data?.Data
					})
				})
		}
	}
}
</script>
<style>
.signin-page .card-md {
  width: 384px;
}
</style>

<style scoped>
.captcha-widget {
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  min-height: 65px;
}
</style>
